import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Settings, 
  Database, 
  Users,
  Shield,
  Palette,
  CreditCard,
  Globe,
  Key,
  Building,
  Lock,
  Activity,
  Cloud,
  Code,
  AlertTriangle,
  CheckCircle,
  Info,
  Plus,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  Copy,
  RefreshCw,
  Save,
  Upload,
  Download,
  ExternalLink,
  Zap,
  Workflow,
  Bell,
  Mail
} from 'lucide-react';
import { toast } from 'react-hot-toast';

// Import configuration components
import { 
  TenantManagement, 
  AuthenticationUsers, 
  BillingSubscriptions 
} from './ConfigurationContent';
import { 
  BrandingDomains, 
  GlobalIntegrations, 
  SecurityPrivacy, 
  DatabaseConfiguration 
} from './ConfigurationSections';
import { 
  WorkflowTemplates, 
  MonitoringAlerts, 
  SystemSettings 
} from './ConfigurationExtras';

const API = process.env.REACT_APP_BACKEND_URL || '';

const Configuration = () => {
  const [activeSection, setActiveSection] = useState('tenants');
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  
  // Multi-tenant Configuration State
  const [tenants, setTenants] = useState([]);
  const [newTenant, setNewTenant] = useState({
    name: '',
    subdomain: '',
    customDomain: '',
    plan: 'starter',
    adminEmail: '',
    maxUsers: 10,
    maxJobs: 100,
    maxStorage: 1024, // MB
    features: {
      aiSourcing: true,
      workflows: true,
      integrations: true,
      analytics: true,
      customBranding: false,
      apiAccess: false,
      sso: false,
      advancedSecurity: false
    }
  });
  
  const [systemConfig, setSystemConfig] = useState({
    databaseMode: 'shared', // shared, isolated, hybrid
    authProvider: 'internal', // internal, oauth, sso
    billingProvider: 'stripe',
    defaultPlan: 'starter',
    enableSignup: true,
    enableTrials: true,
    trialDays: 14,
    maintenanceMode: false,
    globalResourceLimits: {
      maxTenantsPerInstance: 1000,
      maxUsersPerTenant: 10000,
      maxJobsPerTenant: 50000,
      maxStoragePerTenant: 10240 // MB
    }
  });
  
  const [integrationConfig, setIntegrationConfig] = useState({
    openai: { enabled: false, apiKey: '', model: 'gpt-4o' },
    anthropic: { enabled: false, apiKey: '', model: 'claude-3-sonnet' },
    gemini: { enabled: false, apiKey: '', model: 'gemini-pro' },
    stripe: { enabled: false, publishableKey: '', secretKey: '' },
    sendgrid: { enabled: false, apiKey: '', fromEmail: '' },
    slack: { enabled: false, botToken: '', signingSecret: '' },
    linkedin: { enabled: false, clientId: '', clientSecret: '' },
    indeed: { enabled: false, apiKey: '' },
    workday: { enabled: false, clientId: '', clientSecret: '' },
    servicenow: { enabled: false, instanceUrl: '', username: '', password: '' }
  });
  
  const [securityConfig, setSecurityConfig] = useState({
    passwordPolicy: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSymbols: false,
      maxAge: 90 // days
    },
    sessionConfig: {
      timeout: 24, // hours
      multiSession: true,
      secureOnly: true
    },
    dataEncryption: {
      enabled: true,
      algorithm: 'AES-256',
      keyRotation: 30 // days
    },
    auditLogging: {
      enabled: true,
      retentionDays: 365,
      logLevel: 'INFO'
    },
    ipWhitelist: {
      enabled: false,
      allowedIPs: []
    }
  });

  useEffect(() => {
    fetchTenants();
    fetchSystemConfig();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/tenants`);
      setTenants(response.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      // Mock data for development
      setTenants([
        {
          id: '1',
          name: 'Acme Corporation',
          subdomain: 'acme',
          customDomain: 'talent.acme.com',
          plan: 'enterprise',
          status: 'active',
          users: 25,
          jobs: 150,
          storageUsed: 2048,
          createdAt: '2024-01-15',
          lastActive: '2024-01-20'
        },
        {
          id: '2',
          name: 'TechStart Inc',
          subdomain: 'techstart',
          customDomain: '',
          plan: 'professional',
          status: 'active',
          users: 8,
          jobs: 45,
          storageUsed: 512,
          createdAt: '2024-01-18',
          lastActive: '2024-01-20'
        }
      ]);
    }
  };

  const fetchSystemConfig = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/system-config`);
      setSystemConfig(response.data || systemConfig);
    } catch (error) {
      console.error('Error fetching system config:', error);
    }
  };

  const previewPurge = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/api/admin/purge-preview`, {
        ...purgeOptions,
        dryRun: true
      });
      setPreviewResults(response.data);
      toast.success('Purge preview generated');
    } catch (error) {
      console.error('Error previewing purge:', error);
      toast.error('Failed to generate purge preview');
    } finally {
      setIsLoading(false);
    }
  };

  const executePurge = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/api/admin/execute-purge`, {
        ...purgeOptions,
        dryRun: false
      });
      
      toast.success(`Purge completed! Deleted ${response.data.totalDeleted} records`);
      setShowConfirmDialog(false);
      setPreviewResults(null);
      fetchDatabaseStats(); // Refresh stats
    } catch (error) {
      console.error('Error executing purge:', error);
      toast.error('Failed to execute purge operation');
    } finally {
      setIsLoading(false);
    }
  };

  const createBackup = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/api/admin/create-backup`);
      toast.success(`Backup created: ${response.data.backupFile}`);
      
      // Trigger download
      const downloadUrl = `${API}/api/admin/download-backup/${response.data.backupFile}`;
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup');
    } finally {
      setIsLoading(false);
    }
  };

  const resetDatabase = async () => {
    if (window.confirm('⚠️ WARNING: This will delete ALL candidate data but preserve jobs. Type "RESET" to confirm')) {
      const confirmation = prompt('Type RESET to confirm:');
      if (confirmation === 'RESET') {
        setIsLoading(true);
        try {
          const response = await axios.post(`${API}/api/admin/reset-database`);
          toast.success(`Database reset complete! Deleted ${response.data.totalDeleted} records`);
          fetchDatabaseStats();
        } catch (error) {
          console.error('Error resetting database:', error);
          toast.error('Failed to reset database');
        } finally {
          setIsLoading(false);
        }
      } else {
        toast.error('Reset cancelled - incorrect confirmation');
      }
    }
  };

  const sections = [
    { id: 'tenants', label: 'Tenant Management', icon: Building },
    { id: 'authentication', label: 'Authentication & Users', icon: Users },
    { id: 'billing', label: 'Billing & Subscriptions', icon: CreditCard },
    { id: 'branding', label: 'Branding & Domains', icon: Palette },
    { id: 'integrations', label: 'Global Integrations', icon: Zap },
    { id: 'workflows', label: 'Workflow Templates', icon: Workflow },
    { id: 'security', label: 'Security & Privacy', icon: Shield },
    { id: 'database', label: 'Database Configuration', icon: Database },
    { id: 'monitoring', label: 'Monitoring & Alerts', icon: Activity },
    { id: 'system', label: 'System Settings', icon: Settings },
  ];

  const purgeTypeOptions = [
    { value: 'orphaned_only', label: 'Clean Orphaned Data', risk: 'low', description: 'Remove broken references (safest)' },
    { value: 'inactive_candidates', label: 'Inactive Candidates', risk: 'medium', description: 'Remove candidates not in active recruitment' },
    { value: 'by_source', label: 'By Source Platform', risk: 'medium', description: 'Remove candidates from specific source' },
    { value: 'by_age', label: 'By Age', risk: 'medium', description: 'Remove candidates older than X days' },
    { value: 'test_data', label: 'Test/Mock Data', risk: 'low', description: 'Remove obvious test and mock candidates' },
    { value: 'all_candidates', label: 'All Candidates', risk: 'high', description: 'Remove ALL candidates (preserve jobs)' },
  ];

  return (
    <div className="space-y-6" data-testid="configuration-page">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Settings className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Multi-Tenant Platform Configuration</h1>
              <p className="text-gray-600">Manage tenants, authentication, billing, and platform-wide settings</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
              Multi-Tenant Mode: Active
            </div>
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
              {tenants.length} Tenants
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md p-4 space-y-2">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-500'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {activeSection === 'tenants' && (
            <TenantManagement 
              tenants={tenants}
              newTenant={newTenant}
              setNewTenant={setNewTenant}
              setShowModal={setShowModal}
              setModalType={setModalType}
              setSelectedTenant={setSelectedTenant}
            />
          )}

          {activeSection === 'authentication' && (
            <AuthenticationUsers 
              systemConfig={systemConfig}
              setSystemConfig={setSystemConfig}
            />
          )}

          {activeSection === 'billing' && (
            <BillingSubscriptions 
              systemConfig={systemConfig}
              setSystemConfig={setSystemConfig}
            />
          )}

          {activeSection === 'branding' && (
            <BrandingDomains />
          )}

          {activeSection === 'integrations' && (
            <GlobalIntegrations 
              integrationConfig={integrationConfig}
              setIntegrationConfig={setIntegrationConfig}
            />
          )}

          {activeSection === 'workflows' && (
            <WorkflowTemplates />
          )}

          {activeSection === 'security' && (
            <SecurityPrivacy 
              securityConfig={securityConfig}
              setSecurityConfig={setSecurityConfig}
            />
          )}

          {activeSection === 'database' && (
            <DatabaseConfiguration 
              systemConfig={systemConfig}
              setSystemConfig={setSystemConfig}
            />
          )}

          {activeSection === 'monitoring' && (
            <MonitoringAlerts />
          )}

          {activeSection === 'system' && (
            <SystemSettings 
              systemConfig={systemConfig}
              setSystemConfig={setSystemConfig}
            />
          )}
        </div>
      </div>

      {/* Tenant Creation/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {modalType === 'create-tenant' ? 'Create New Tenant' : 
                 modalType === 'edit-tenant' ? 'Edit Tenant' : 'Tenant Details'}
              </h2>
            </div>
            
            <div className="p-6">
              {modalType === 'create-tenant' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organization Name
                      </label>
                      <input
                        type="text"
                        value={newTenant.name}
                        onChange={(e) => setNewTenant({...newTenant, name: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="Acme Corporation"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subdomain
                      </label>
                      <input
                        type="text"
                        value={newTenant.subdomain}
                        onChange={(e) => setNewTenant({...newTenant, subdomain: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="acme"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Admin Email
                      </label>
                      <input
                        type="email"
                        value={newTenant.adminEmail}
                        onChange={(e) => setNewTenant({...newTenant, adminEmail: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="admin@acme.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subscription Plan
                      </label>
                      <select
                        value={newTenant.plan}
                        onChange={(e) => setNewTenant({...newTenant, plan: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      >
                        <option value="starter">Starter</option>
                        <option value="professional">Professional</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {modalType === 'tenant-details' && selectedTenant && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Organization:</span>
                      <span className="font-medium ml-2">{selectedTenant.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                        selectedTenant.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedTenant.status}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Plan:</span>
                      <span className="font-medium ml-2 capitalize">{selectedTenant.plan}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Users:</span>
                      <span className="font-medium ml-2">{selectedTenant.users}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white"
              >
                {modalType === 'tenant-details' ? 'Close' : 'Cancel'}
              </button>
              {modalType !== 'tenant-details' && (
                <button
                  onClick={() => {
                    toast.success('Tenant saved successfully');
                    setShowModal(false);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium"
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Configuration;